import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "columnSelect",
    "headerRowToggle",
    "mappedIcon",
    "unmappedIcon",
    "mappedCount",
    "totalCount",
    "previewData",
    "warningAlert",
    "infoAlert",
    "newCustomAttributeModal",
    "newCustomAttributeButton",
    "newCustomAttributeInput",
    "nextButton"
  ]

  static values = {
    campaignPresent: Boolean,
    columnMatching: Object,
    importId: String
  }

  connect() {
    this.updateUI()
    this.setupCustomAttributeHandlers()
  }

  // Setup event handlers for custom attributes
  setupCustomAttributeHandlers() {
    if (this.hasNewCustomAttributeButtonTarget) {
      this.newCustomAttributeButtonTarget.addEventListener("click", () => {
        this.createCustomAttribute()
      })
    }
  }

  // Handle column selection change
  handleColumnChange(event) {
    const select = event.target

    // Check if user selected "new_custom_attribute"
    if (select.value === "new_custom_attribute") {
      this.openCustomAttributeModal()
      return
    }

    this.updateUI()
    this.saveConfiguration()
  }

  // Open modal for creating new custom attribute
  openCustomAttributeModal() {
    if (this.hasNewCustomAttributeModalTarget && window.App && window.App.modal) {
      window.App.modal($(this.newCustomAttributeModalTarget), "show")
    } else {
      // Fallback for Bootstrap modal
      const modal = new bootstrap.Modal(this.newCustomAttributeModalTarget)
      modal.show()
    }
  }

  // Create new custom attribute
  async createCustomAttribute() {
    if (!this.hasNewCustomAttributeInputTarget || !this.hasNewCustomAttributeButtonTarget) {
      return
    }

    const attributeLabel = this.newCustomAttributeInputTarget.value.trim()
    if (!attributeLabel) {
      return
    }

    // Disable the button during request
    this.newCustomAttributeButtonTarget.setAttribute("disabled", true)

    try {
      const response = await fetch("/leads/custom-attributes", {
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
        },
        body: JSON.stringify({
          lead_custom_attribute: {
            label: attributeLabel
          }
        }),
      })

      const data = await response.json()

      if (data.errors) {
        this.displayFlash("danger", `Failed to create attribute: ${data.errors}`)
        return
      }

      // Success - add the new attribute to all selects
      this.addCustomAttributeToSelects(data.label, data.slug)
      this.resetSelectWithNewCustomAttribute(`lead_custom_attribute_${data.slug}`)
      this.hideCustomAttributeModal()
      this.clearCustomAttributeInput()
      this.displayFlash("success", `Attribute "${data.label}" created successfully`)

      // Update UI and save configuration
      this.updateUI()
      this.saveConfiguration()

    } catch (error) {
      console.error("Failed to create custom attribute:", error)
      this.displayFlash("danger", "Failed to create attribute. Please try again.")
    } finally {
      this.newCustomAttributeButtonTarget.removeAttribute("disabled")
    }
  }

  // Add new custom attribute to all select elements
  addCustomAttributeToSelects(label, slug) {
    const value = `lead_custom_attribute_${slug}`

    this.columnSelectTargets.forEach(select => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label

      // Insert before the "new_custom_attribute" option
      const newAttrOption = select.querySelector('option[value="new_custom_attribute"]')
      if (newAttrOption) {
        select.insertBefore(option, newAttrOption)
      } else {
        select.appendChild(option)
      }
    })
  }

  // Reset the select that had "new_custom_attribute" selected
  resetSelectWithNewCustomAttribute(value) {
    this.columnSelectTargets.forEach(select => {
      if (select.value === "new_custom_attribute") {
        select.value = value
      }
    })
  }

  // Hide the custom attribute modal
  hideCustomAttributeModal() {
    if (this.hasNewCustomAttributeModalTarget) {
      if (window.App && window.App.modal) {
        window.App.modal($(this.newCustomAttributeModalTarget), "hide")
      } else {
        // Fallback for Bootstrap modal
        const modal = bootstrap.Modal.getInstance(this.newCustomAttributeModalTarget)
        if (modal) {
          modal.hide()
        }
      }
    }
  }

  // Clear the custom attribute input
  clearCustomAttributeInput() {
    if (this.hasNewCustomAttributeInputTarget) {
      this.newCustomAttributeInputTarget.value = ""
    }
  }

  // Display flash message (assuming you have a global App.displayFlash method)
  displayFlash(type, message) {
    window.App.displayFlash(type, message)
  }

  // Toggle header row visibility
  toggleHeaderRow(event) {
    const isChecked = event.target.checked

    // Toggle preview row visibility
    this.previewDataTargets.forEach(element => {
      if (element.dataset.previewRow === "0") {
        element.style.display = isChecked ? "none" : "inline"
      }
    })

    this.saveConfiguration()
  }

  // Private: Update all UI elements
  updateUI() {
    this.updateStatusIcons()
    this.updateMappingCounts()
    this.disableSelectedOptions()
    this.updateAlerts()
    this.updateNextButton()
  }

  updateNextButton() {
    if (!this.hasNextButtonTarget) return

    const hasRequiredColumns = this.checkRequiredColumns()

    if (hasRequiredColumns) {
      this.nextButtonTarget.style.pointerEvents = 'auto'
      this.nextButtonTarget.style.opacity = '1'
      this.nextButtonTarget.classList.remove('disabled')
    } else {
      this.nextButtonTarget.style.pointerEvents = 'none'
      this.nextButtonTarget.style.opacity = '0.5'
      this.nextButtonTarget.classList.add('disabled')
    }
  }

  // Private: Update alert visibility based on required columns
  updateAlerts() {
    const hasRequiredColumns = this.checkRequiredColumns()
    const allFieldsMapped = this.getMappedColumns().length === this.columnSelectTargets.length

    if (this.hasWarningAlertTarget && this.hasInfoAlertTarget) {
      this.warningAlertTarget.style.display = hasRequiredColumns ? "none" : ""
      this.infoAlertTarget.style.display = (hasRequiredColumns && !allFieldsMapped) ? "" : "none"
    }
  }

  // Private: Check if required columns are matched based on campaign presence
  checkRequiredColumns() {
    const columnMatching = this.buildColumnMatching()

    if (this.campaignPresentValue) {
      // When campaign is present, only email is required
      return columnMatching.email !== undefined
    } else {
      // When campaign is NOT present, requires:
      // email OR (name fields AND company fields)
      const hasEmail = columnMatching.email !== undefined

      const hasNameFields = columnMatching.full_name !== undefined ||
        (columnMatching.first_name !== undefined && columnMatching.last_name !== undefined)

      const hasCompanyFields = columnMatching.website !== undefined ||
        columnMatching.company !== undefined

      return hasEmail || (hasNameFields && hasCompanyFields)
    }
  }

  // Private: Update status icons for each column
  updateStatusIcons() {
    this.columnSelectTargets.forEach((select, index) => {
      const isMapped = this.isColumnMapped(select)
      const mappedIcon = this.mappedIconTargets[index]
      const unmappedIcon = this.unmappedIconTargets[index]

      if (isMapped) {
        mappedIcon?.classList.remove("d-none")
        unmappedIcon?.classList.add("d-none")
      } else {
        mappedIcon?.classList.add("d-none")
        unmappedIcon?.classList.remove("d-none")
      }
    })
  }

  // Private: Update the mapping count display
  updateMappingCounts() {
    const mappedCount = this.getMappedColumns().length
    const totalCount = this.columnSelectTargets.length

    if (this.hasMappedCountTarget) {
      this.mappedCountTarget.textContent = mappedCount
    }
    if (this.hasTotalCountTarget) {
      this.totalCountTarget.textContent = ` of ${totalCount} columns mapped`
    }
  }

  // Private: Disable already selected options in other selects
  disableSelectedOptions() {
    const selectedValues = this.getSelectedValues()

    this.columnSelectTargets.forEach(select => {
      Array.from(select.options).forEach(option => {
        const shouldDisable = option.value !== "" &&
          option.value !== select.value &&
          option.value !== "new_custom_attribute" &&
          selectedValues.includes(option.value)
        option.disabled = shouldDisable
      })
    })
  }

  // Private: Save configuration via AJAX
  async saveConfiguration() {
    const columnMatching = this.buildColumnMatching()
    const headerRow = this.hasHeaderRowToggleTarget ? this.headerRowToggleTarget.checked : false

    try {
      const response = await fetch(`/leads-imports/${this.importIdValue}/configuration`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          leads_import: {
            column_matching: JSON.stringify(columnMatching),
            header_row: headerRow
          }
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      // Update stored column matching value
      this.columnMatchingValue = columnMatching

      // Handle validation errors if any
      if (data.errors && data.errors.length > 0) {
        console.error('Validation errors:', data.errors)
      }

    } catch (error) {
      console.error('Failed to save configuration:', error)
    }
  }

  // Private: Build column matching object from current selections
  buildColumnMatching() {
    const columnMatching = {}

    this.columnSelectTargets.forEach(select => {
      if (this.isValidMapping(select)) {
        const columnIndex = this.extractColumnIndex(select)
        columnMatching[select.value] = columnIndex
      }
    })

    return columnMatching
  }

  // Private: Helper methods
  isColumnMapped(select) {
    return select.value && select.value !== "" && select.value !== "new_custom_attribute"
  }

  isValidMapping(select) {
    return select.value &&
      select.value !== "" &&
      select.value !== "new_custom_attribute"
  }

  extractColumnIndex(select) {
    const match = select.name.match(/\d+/)
    return match ? match[0] : null
  }

  getMappedColumns() {
    return this.columnSelectTargets.filter(select => this.isColumnMapped(select))
  }

  getSelectedValues() {
    return this.columnSelectTargets
      .map(select => select.value)
      .filter(value => value && value !== "" && value !== "new_custom_attribute")
  }

  getCSRFToken() {
    const tokenElement = document.querySelector('meta[name="csrf-token"]')
    return tokenElement ? tokenElement.content : ''
  }
}
